// client/src/contexts/AuthContext.jsx
import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

// Créer le contexte d'authentification
const AuthContext = createContext();

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => useContext(AuthContext);

// Fournisseur du contexte d'authentification
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialisation: vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (storedToken && storedUser) {
        try {
          // Configurer les en-têtes avec le token
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          
          // Vérifier la validité du token
          const response = await axios.get('/api/auth/verify');
          
          if (response.data.success) {
            setUser(JSON.parse(storedUser));
            setIsAuthenticated(true);
          } else {
            // Token invalide, nettoyer le localStorage
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      
      setLoading(false);
    };

    initAuth();
  }, []);

  // Fonction de connexion
  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // Enregistrer le token et l'utilisateur dans le localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Configurer les en-têtes avec le token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Mettre à jour l'état
        setUser(user);
        setIsAuthenticated(true);
        
        return { success: true, user };
      } else {
        return { success: false, error: response.data.error };
      }
    } catch (error) {
      console.error('Erreur lors de la connexion:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Erreur de connexion' 
      };
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    // Nettoyer le localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // Supprimer l'en-tête d'autorisation
    delete axios.defaults.headers.common['Authorization'];
    
    // Mettre à jour l'état
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * Met à jour l'utilisateur courant EN PLACE (état + localStorage).
   *
   * Sert après un changement de mot de passe : le drapeau `mustChangePassword` doit retomber
   * immédiatement. Sans cela, le bandeau « mot de passe par défaut » resterait affiché jusqu'au
   * prochain login — l'utilisateur vient de faire ce qu'on lui demandait, et le logiciel continue
   * de le lui réclamer. On ne le déconnecte pas non plus : le jeton reste parfaitement valide.
   */
  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  };

  // Vérifier si l'utilisateur a un rôle spécifique
  const hasRole = (requiredRole) => {
    if (!user) return false;
    return user.role === requiredRole;
  };

  // Valeurs à fournir au contexte
  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    hasRole,
    updateUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
